<template>
  <div class="erp-configuration-page">
    <card :title="$t('tools.erp_integration.erp_configuration')" :hide-title="true" id="erp-configuration">
      <div class="configuration-header">
        <h1 class="configuration-header__title">{{ $t("tools.erp_integration.erp_configuration") }}</h1>
        <div class="configuration-header__actions">
          <router-link
            to="/tools/erp-integration"
            class="btn btn-md btn-secondary configuration-header__icon-button"
            :aria-label="$t('common.back_to_list')"
            :title="$t('common.back_to_list')"
          >
            <span class="dx-icon dx-icon-arrowleft" aria-hidden="true"></span>
          </router-link>
        </div>
      </div>

      <div class="erp-name-block form-group">
        <label for="erp-name" class="control-label">{{ $t("tools.erp_integration.erp_name") }}</label>
        <input
          id="erp-name"
          v-model="erpName"
          type="text"
          class="form-control"
          :class="{ 'field-error': showErpNameError }"
          :placeholder="$t('tools.erp_integration.erp_name_placeholder')"
        />
        <small v-if="showErpNameError" class="text-danger d-block pt-1">
          {{ $t("tools.erp_integration.erp_name_required") }}
        </small>
      </div>

      <setting-group
        v-if="erpSettingsGroup"
        :data="erpSettingsGroup"
        :visible-codes="erpConfigurationVisibleCodes"
      />

      <div v-if="erpSettingsGroup" class="alert alert-info mt-3 mb-0" role="alert">
        {{ $t("tools.erp_integration.reception_send_mode_help") }}
      </div>

      <div v-else class="placeholder-message">
        {{ $t("tools.erp_integration.erp_settings_group_not_found") }}
      </div>
    </card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SettingGroup from "@/pages/tools/settings/partials/SettingGroup";

export default {
  name: "ErpConfigurationPage",
  components: {
    SettingGroup,
  },
  data() {
    return {
      erpName: "",
      erpSettingsGroup: null,
      erpNameCode: "ERP_NAME",
      erpConfigurationVisibleCodes: [
        "ERP_INTEGRATION_ENABLED",
        "ERP_API_SERVER",
        "ERP_USERNAME",
        "ERP_PASSWORD"
      ],
      showErpNameError: false,
    };
  },
  computed: {
    ...mapGetters({
      settingGroups: "settings/settingGroups",
    }),
  },
  methods: {
    ...mapActions({
      getGroups: "settings/getSettingGroups",
    }),
    resolveErpSettingsGroup() {
      const groups = this.settingGroups || [];

      const sourceGroup =
        groups.find((group) => {
          const code = (group?.code || "").toUpperCase();
          const itemCodes = (group?.items || []).map((item) => (item?.code || "").toUpperCase());

          return code === "ERP_INTEGRATION"
            || itemCodes.includes("ERP_INTEGRATION_ENABLED")
            || itemCodes.includes("ERP_NAME");
        }) || null;

      this.erpSettingsGroup = sourceGroup ? JSON.parse(JSON.stringify(sourceGroup)) : null;
      this.cacheOriginalCheckboxName();
      this.applyErpNameToCheckboxLabel();
    },
    cacheOriginalCheckboxName() {
      if (!this.erpSettingsGroup?.items?.length) {
        return;
      }

      const enabledSetting = this.erpSettingsGroup.items.find(
        (item) => item?.code === "ERP_INTEGRATION_ENABLED"
      );

      if (!enabledSetting) {
        return;
      }
    },
    getErpNameSetting() {
      if (!this.erpSettingsGroup?.items?.length) {
        return null;
      }

      return (
        this.erpSettingsGroup.items.find((item) => item?.code === this.erpNameCode) || null
      );
    },
    syncErpNameFromSetting() {
      const erpSetting = this.getErpNameSetting();
      this.erpName = (erpSetting?.value || "").toString();
    },
    syncErpNameToSetting() {
      const erpSetting = this.getErpNameSetting();

      if (!erpSetting) {
        return;
      }

      erpSetting.value = this.erpName;
    },
    attachErpNameValidator() {
      const erpSetting = this.getErpNameSetting();

      if (!erpSetting) {
        return;
      }

      erpSetting.is_required = 1;
      erpSetting.validate = () => {
        const isValid = (this.erpName || "").trim().length > 0;
        this.showErpNameError = !isValid;
        return isValid;
      };
    },
    applyErpNameToCheckboxLabel() {
      if (!this.erpSettingsGroup?.items?.length) {
        return;
      }

      const enabledSetting = this.erpSettingsGroup.items.find(
        (item) => item?.code === "ERP_INTEGRATION_ENABLED"
      );

      if (!enabledSetting) {
        return;
      }

      const trimmedName = (this.erpName || "").trim();

      enabledSetting.name = trimmedName
        ? this.$t("tools.erp_integration.integration_enabled_with_name", { name: trimmedName })
        : this.$t("tools.erp_integration.integration_enabled_default");
    },
  },
  mounted() {
    this.getGroups().then(() => {
      this.resolveErpSettingsGroup();
      this.syncErpNameFromSetting();
      this.attachErpNameValidator();
    });
  },
  watch: {
    settingGroups: {
      handler() {
        this.resolveErpSettingsGroup();
        this.syncErpNameFromSetting();
        this.attachErpNameValidator();
      },
      deep: true,
    },
    erpName() {
      this.showErpNameError = false;
      this.syncErpNameToSetting();
      this.applyErpNameToCheckboxLabel();
    },
  },
};
</script>

<style scoped>
.configuration-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.configuration-header__title {
  margin: 0;
  color: #323232;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.configuration-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.configuration-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
}

.erp-name-block {
  max-width: 560px;
}

.erp-name-block .control-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #30465f;
}

.placeholder-message {
  min-height: 120px;
  display: flex;
  align-items: center;
  color: #5f6b7a;
  font-size: 14px;
}
</style>
